import { Icon20ChevronLeftOutline, Icon20ChevronRightOutline } from '@vkontakte/icons';
import { Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isRebranding } from 'reactApp/appHelpers/featuresHelpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './SliderModal.css';
import type { SliderModalProps } from './SliderModal.types';

export const SliderModal = memo<SliderModalProps>(
    ({ slides, qaId, primaryButtonText, primaryButtonOnClick, onClose, onShow, onCancel, isRebrandingQuotaLanding }) => {
        const isMobile = EnvironmentSelectors.isPhone();
        const scrollRef = useRef<HTMLDivElement>(null);
        const [activeSlide, setActiveSlide] = useState(0);

        useEffect(() => {
            onShow?.(activeSlide + 1);
        }, [activeSlide, onShow]);

        const handleClose = useCallback(() => {
            onCancel?.(activeSlide + 1);
            onClose?.();
        }, [activeSlide, onClose, onCancel]);

        const content = useMemo(() => {
            return (
                <>
                    <div
                        className={classNames(styles.slider, {
                            [styles.slider_rebranding]: isRebranding,
                            [styles.slider_mobile_rebranding]: isRebranding && isMobile,
                            [styles.slider_rebrandingQuotaLanding]: isRebrandingQuotaLanding,
                            [styles.slider_mobile_rebrandingQuotaLanding]: isRebrandingQuotaLanding && isMobile,
                        })}
                    >
                        <Swiper
                            simulateTouch
                            slidesPerView={1}
                            loop={false}
                            navigation={{
                                nextEl: `.${styles.arrowNext}`,
                                prevEl: `.${styles.arrowPrev}`,
                                disabledClass: styles.arrowDisabled,
                            }}
                            pagination={{
                                clickable: true,
                                bulletClass: styles.bullet,
                                bulletActiveClass: styles.bulletActive,
                                clickableClass: styles.bulletWrapper,
                                ...(isMobile && {
                                    el: `.${styles.pagination}`,
                                }),
                            }}
                            modules={[Navigation, Pagination]}
                            onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
                        >
                            {slides.map((item) => (
                                <SwiperSlide key={item.id}>
                                    <img src={isMobile ? item.srcMobile : item.src} className={styles.image} alt={item.title} />
                                    <div className={styles.content}>
                                        <Text className={styles.title}>{item.title}</Text>
                                        <Spacing size={12} />
                                        <Text className={styles.text}>{item.text}</Text>
                                        <Spacing size={32} />
                                        {primaryButtonText && (
                                            <Button
                                                theme="vk"
                                                primary
                                                sizeMode={isMobile ? ButtonSizeMode.big : ButtonSizeMode.small}
                                                className={styles.button}
                                                onClick={() => primaryButtonOnClick?.(activeSlide + 1)}
                                            >
                                                {primaryButtonText}
                                            </Button>
                                        )}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <div className={styles.arrowNext}>
                            <Icon20ChevronRightOutline />
                        </div>
                        <div className={styles.arrowPrev}>
                            <Icon20ChevronLeftOutline />
                        </div>
                        {isMobile && <div className={styles.pagination} />}
                    </div>
                </>
            );
        }, [activeSlide, setActiveSlide, slides, primaryButtonOnClick, primaryButtonText, isMobile]);

        if (isMobile) {
            return (
                <MobileDialog
                    id={qaId || 'slider-dlg-mobile'}
                    onClose={handleClose}
                    mod="base"
                    topmost
                    scrollRef={scrollRef}
                    contextPadding="zero"
                    disableScrolling
                    closeOnDimmerClick
                    isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                >
                    <div ref={scrollRef} className={styles.rootMobile}>
                        {content}
                    </div>
                </MobileDialog>
            );
        }

        return (
            <Dialog
                open
                closeOnDimmerClick={false}
                id="slider-dlg"
                qaId={qaId}
                closable
                size="large"
                className={styles.root}
                onCancel={handleClose}
                isRebrandingQuotaLanding={isRebrandingQuotaLanding}
            >
                {content}
            </Dialog>
        );
    }
);

SliderModal.displayName = 'SliderModal';
